/**
 * **Grup harga** — "tarif mal" vs "tarif ruko", milik BRAND.
 *
 * ### ⭐⭐ Kenapa grup, bukan harga khusus per outlet
 * Tanpa grup, menaikkan harga di 12 outlet mal berarti 12 baris harga per produk — dikalikan 300
 * produk = 3.600 baris yang harus diubah bersamaan. Yang terjadi di lapangan bukan "semuanya
 * diubah", melainkan **sebagian**: dua outlet tertinggal, dan yang menemukannya adalah tamu yang
 * membandingkan struk.
 *
 * ⇒ Harga ditulis SEKALI ke grupnya; outlet cukup menunjuk grup mana yang ia ikuti.
 *
 * ### ⚠️ Grup mana yang dipakai outlet disetel DI LAYAR OUTLET
 * `price_group_id` adalah kolom milik **outlet**, dan di sanalah ia disunting. Layar ini
 * menampilkan anggotanya supaya "siapa saja yang ikut tarif ini?" terjawab sekilas — pertanyaan
 * yang selalu datang tepat sebelum harga dinaikkan.
 *
 * ### ⛔ Urutan menang saat harga dicari (lihat `pos-core/catalog.ts`)
 * harga khusus OUTLET → harga GRUP → harga umum. Satu baris harga hanya boleh menyasar satu
 * sasaran (CHECK `product_price_one_target`): baris yang menyasar keduanya tak punya jawaban
 * tunggal atas "ini harga untuk siapa?".
 */
import { Alert, Card, Empty, Form, Input, Space, Switch, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import type { Outlet, PriceGroup } from '../api/client';
import { canWrite } from '../api/session';
import { BrandPicker } from '../components/BrandPicker';
import { CrudPanel } from '../components/CrudPanel';
import { PriceMatrix } from '../components/PriceMatrix';

export function PriceGroups() {
  const { t } = useTranslation();
  const boleh = canWrite();
  const [brandId, setBrandId] = useState<string | null>(null);
  const [outlet, setOutlet] = useState<readonly Outlet[]>([]);
  /*
   * ⚠️ Daftar grup dimuat DUA KALI: sekali oleh `CrudPanel` (untuk tabelnya), sekali di sini
   * (untuk pemilih di panel pengisian). Disengaja — memaksa `CrudPanel` mengangkat daftarnya
   * keluar berarti mengubah komponen yang dipakai 11 layar demi satu layar.
   */
  const [grup, setGrup] = useState<readonly PriceGroup[]>([]);

  useEffect(() => { void api.outlets(undefined, true).then(setOutlet).catch(() => undefined); }, []);
  useEffect(() => {
    if (brandId === null) { setGrup([]); return; }
    void api.priceGroups(brandId, true).then(setGrup).catch(() => undefined);
  }, [brandId]);

  /* ⛔ `includeInactive`: layar pengelola — grup yang dimatikan harus tetap bisa dinyalakan. */
  const muat = useCallback(async (): Promise<readonly PriceGroup[]> => {
    if (brandId === null) return [];
    return api.priceGroups(brandId, true);
  }, [brandId]);

  const kolom: ColumnsType<PriceGroup> = [
    {
      title: t('master.code'),
      dataIndex: 'code',
      width: 160,
      render: (v: string) => <b data-testid={`grup-${v}`}>{v}</b>,
    },
    { title: t('master.name'), dataIndex: 'name' },
    {
      /*
       * ⭐ Anggota tampil sebagai kolom. "Grup ini dipakai siapa?" adalah pertanyaan yang datang
       * tepat sebelum harga dinaikkan — dan menjawabnya dengan membuka 12 outlet satu per satu
       * berarti jawabannya tak pernah benar-benar dicari.
       */
      title: t('priceGroup.members'),
      key: 'anggota',
      render: (_: unknown, g: PriceGroup) => {
        const anggota = outlet.filter((o) => o.price_group_id === g.id);
        return anggota.length === 0 ? (
          /* ⚠️ Grup tanpa anggota bukan galat — tapi harga yang ditulis ke sana tak dipakai
             siapa pun, dan itu harus terlihat sebelum orang menulis 300 baris ke dalamnya. */
          <Tag color="orange" data-testid={`grup-kosong-${g.code}`}>{t('priceGroup.noMember')}</Tag>
        ) : (
          <Space size={4} wrap data-testid={`anggota-${g.code}`}>
            {anggota.map((o) => <Tag key={o.id}>{o.code}</Tag>)}
          </Space>
        );
      },
    },
    {
      title: '',
      dataIndex: 'is_active',
      width: 96,
      render: (v: boolean) =>
        v ? <Tag color="green">{t('outlets.active')}</Tag> : <Tag>{t('outlets.inactive')}</Tag>,
    },
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }} data-testid="price-groups">
      <Space>
        <Typography.Text strong>{t('structure.brand')}</Typography.Text>
        <BrandPicker value={brandId} onChange={setBrandId} />
      </Space>
      <Alert type="info" showIcon title={t('priceGroup.note')} data-testid="grup-catatan" />

      {brandId === null ? (
        <Empty description={t('structure.pickBrand')} data-testid="grup-pilih-brand" />
      ) : (
        <CrudPanel<PriceGroup>
          testId="grup"
          title={t('priceGroup.title')}
          subtitle={t('priceGroup.subtitle')}
          canWrite={boleh}
          newLabel={t('priceGroup.new')}
          columns={kolom}
          fetchList={muat}
          searchIn={(g) => `${g.code} ${g.name}`}
          emptyText={t('priceGroup.empty')}
          renderFields={(_f, editing) => (
            <>
              {editing === null ? (
                <Form.Item
                  name="code"
                  label={t('master.code')}
                  rules={[{ required: true, pattern: /^[A-Za-z0-9_-]+$/, message: t('structure.codeRule') }]}
                  tooltip={t('structure.codeFixed')}
                >
                  <Input maxLength={40} data-testid="grup-kode" />
                </Form.Item>
              ) : (
                <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t('structure.codeLocked')} />
              )}
              <Form.Item name="name" label={t('master.name')} rules={[{ required: true, min: 2 }]}>
                <Input maxLength={120} data-testid="grup-nama" />
              </Form.Item>
              {/* ⚠️ Menunjuk grup terjadi di layar OUTLET — disebut di sini supaya yang mencarinya
                  tak menyimpulkan fiturnya belum ada. */}
              <Alert type="info" showIcon title={t('priceGroup.assignHint')} />
              {editing !== null && (
                <Form.Item name="isActive" label={t('outlets.status')} valuePropName="checked"
                           style={{ marginTop: 16 }}>
                  <Switch
                    data-testid="grup-aktif"
                    checkedChildren={t('outlets.active')}
                    unCheckedChildren={t('outlets.inactive')}
                  />
                </Form.Item>
              )}
            </>
          )}
          toFormValues={(g) => ({ name: g.name, isActive: g.is_active })}
          onCreate={async (v) => {
            if (brandId === null) return;
            await api.createPriceGroup({
              brandId,
              code: String(v['code']).toUpperCase(),
              name: String(v['name']),
            });
          }}
          onUpdate={async (id, v, lama) => {
            await api.updateMaster('price-groups', id, { name: v['name'] });
            if (v['isActive'] !== lama.is_active) {
              await api.setStatus('price-groups', id, v['isActive'] === true);
            }
          }}
        />
      )}

      {/*
        ⭐⭐⭐ **Satu-satunya tempat harga diisi** (Imron, 2026-08-04). Laci produk kini hanya
        MENAMPILKAN riwayatnya.

        ⛔⛔ "Harga umum" ikut jadi SASARAN di sini, dan itu wajib: kalau halaman ini cuma melayani
        grup, memindahkan pengisian ke sini berarti harga umum — yang dipakai setiap outlet yang
        tak masuk grup mana pun — tak punya jalan masuk sama sekali, dan tenant baru tak bisa
        memberi harga apa pun. Itu bukan penyederhanaan, itu penghapusan fitur.

        ⚠️ Berdiri TERPISAH dari laci sunting: laci menjawab "grup ini namanya apa", tabel ini
        menjawab "isinya berapa". Menyatukannya membuat satu laci punya dua makna "tersimpan".
      */}
      {brandId !== null && (
        <Card size="small" data-testid="harga-kartu" title={t('priceGroup.fillTitle')}>
          <PriceMatrix brandId={brandId} groups={grup} />
        </Card>
      )}
    </Space>
  );
}
